import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Calendar, Loader2, Trash2, ChevronDown, ChevronRight, Dumbbell, Clock, Target, Trophy, Star, ArrowRight, Share2, Link2, Check } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BENCHMARKS = [
  { key: '5k_time', label: '5K Run Time', placeholder: 'e.g. 25:00' },
  { key: 'skierg_1k', label: '1K SkiErg', placeholder: 'e.g. 4:30' },
  { key: 'row_1k', label: '1K Row', placeholder: 'e.g. 4:00' },
  { key: 'wall_balls_100', label: '100 Wall Balls', placeholder: 'e.g. 6:00' },
];

const INTENSITY_COLOR = {
  Easy: 'text-green-400 bg-green-400/10 border-green-400/20',
  Moderate: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  Hard: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  Max: 'text-red-400 bg-red-400/10 border-red-400/20',
};

const DAY_TYPE_ICON = {
  Run: '🏃',
  'Station Work': '🏋️',
  Strength: '💪',
  Recovery: '🧘',
  'Race Sim': '🏁',
  Rest: '😴',
};

function PlanViewer({ planDoc, onDelete, onToggleShare, token }) {
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [copied, setCopied] = useState(false);
  const plan = planDoc.plan || {};

  const shareUrl = planDoc.share_token ? `${window.location.origin}/race-plan/${planDoc.share_token}` : '';

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (plan.parse_error) {
    return (
      <div className="border border-zinc-800 p-6 bg-zinc-900/40">
        <p className="text-zinc-400 text-sm">Plan generated but couldn't be parsed. Raw content:</p>
        <pre className="text-zinc-500 text-xs mt-2 whitespace-pre-wrap overflow-auto max-h-96">{plan.raw_text}</pre>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`plan-${planDoc.plan_id}`}>
      {/* Plan Header */}
      <div className="flex items-start justify-between border-b border-zinc-800 pb-4">
        <div>
          <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            {plan.plan_name || 'Your HYROX Plan'}
          </h3>
          <p className="text-zinc-400 text-sm mt-1">{plan.summary}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider px-2 py-0.5 border border-zinc-800" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {planDoc.experience_level || 'intermediate'}
            </span>
            {planDoc.race_date && (
              <span className="text-[10px] text-[#e8ff00] flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Race: {planDoc.race_date}
              </span>
            )}
            <span className="text-[10px] text-zinc-600">Created {new Date(planDoc.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {planDoc.is_shared && shareUrl && (
            <button
              onClick={handleCopyShareLink}
              className="flex items-center gap-1 px-2 py-1 border border-[#e8ff00]/30 text-[#e8ff00] text-[10px] hover:bg-[#e8ff00]/10 transition-colors"
              data-testid={`copy-link-${planDoc.plan_id}`}
            >
              {copied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          )}
          {onToggleShare && (
            <button
              onClick={() => onToggleShare(planDoc.plan_id)}
              className={`flex items-center gap-1 px-2 py-1 border text-[10px] transition-colors ${
                planDoc.is_shared
                  ? 'border-[#e8ff00]/30 text-[#e8ff00] hover:bg-[#e8ff00]/10'
                  : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
              }`}
              data-testid={`share-toggle-${planDoc.plan_id}`}
            >
              <Share2 className="w-3 h-3" />
              {planDoc.is_shared ? 'Shared' : 'Share'}
            </button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(planDoc.plan_id)} className="text-zinc-700 hover:text-red-400 transition-colors p-1" data-testid={`delete-plan-${planDoc.plan_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Weekly Mileage */}
      {plan.weekly_mileage_progression && (
        <div className="flex items-end gap-1 h-16">
          {plan.weekly_mileage_progression.map((m, i) => (
            <div key={`week-${i + 1}`} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-[#e8ff00]/20 border border-[#e8ff00]/30 transition-all"
                style={{ height: `${(m / Math.max(...plan.weekly_mileage_progression)) * 48}px` }}
              />
              <span className="text-[9px] text-zinc-600">W{i + 1}</span>
            </div>
          ))}
          <span className="text-[9px] text-zinc-600 ml-1 self-center">km/wk</span>
        </div>
      )}

      {/* Weeks */}
      <div className="space-y-2">
        {(plan.weeks || []).map((week) => (
          <div key={week.week} className="border border-zinc-800 bg-zinc-900/30 overflow-hidden" data-testid={`week-${week.week}`}>
            <button
              onClick={() => setExpandedWeek(expandedWeek === week.week ? null : week.week)}
              className="w-full flex items-center justify-between px-4 py-3 text-left group hover:bg-zinc-900/60 transition-colors"
              data-testid={`week-toggle-${week.week}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-[#e8ff00] text-lg font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>W{week.week}</span>
                <div>
                  <p className="text-white text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{week.theme}</p>
                  <p className="text-zinc-500 text-xs">{week.focus}</p>
                </div>
              </div>
              {expandedWeek === week.week ? <ChevronDown className="w-4 h-4 text-[#e8ff00]" /> : <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400" />}
            </button>

            {expandedWeek === week.week && (
              <div className="px-4 pb-4 space-y-2 border-t border-zinc-800/50" data-testid={`week-expanded-${week.week}`}>
                {(week.days || []).map((day, di) => (
                  <div key={day.title} className="flex items-start gap-3 py-2.5 border-b border-zinc-800/30 last:border-0">
                    <div className="w-20 flex-shrink-0">
                      <p className="text-zinc-500 text-xs font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{day.day}</p>
                      <p className="text-zinc-700 text-[10px]">{DAY_TYPE_ICON[day.type] || '📋'} {day.type}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{day.title}</p>
                      <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">{day.description}</p>
                      {day.stations_targeted && day.stations_targeted.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {day.stations_targeted.map((s, si) => (
                            <span key={s} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {day.duration_min && (
                        <span className="text-zinc-600 text-[10px] flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {day.duration_min}m
                        </span>
                      )}
                      {day.intensity && (
                        <span className={`text-[10px] px-1.5 py-0.5 border ${INTENSITY_COLOR[day.intensity] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                          {day.intensity}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tips */}
      {plan.tips && plan.tips.length > 0 && (
        <div className="bg-[#e8ff00]/5 border border-[#e8ff00]/20 p-4 mt-4">
          <p className="text-[#e8ff00] text-xs uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Coach's Tips</p>
          {plan.tips.map((tip, i) => (
            <p key={tip} className="text-zinc-400 text-sm flex items-start gap-2 mb-1.5">
              <Star className="w-3 h-3 text-[#e8ff00] mt-1 flex-shrink-0" /> {tip}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RacePlanGenerator() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [raceDate, setRaceDate] = useState('');
  const [benchmarks, setBenchmarks] = useState({});
  const [error, setError] = useState('');

  const fetchPlans = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_URL}/api/race-plan/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setPlans(await resp.json());
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const pollPlanStatus = async (planId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const resp = await fetch(`${API_URL}/api/race-plan/${planId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) continue;
        const data = await resp.json();
        if (data.status === 'ready') return true;
        if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      } catch (e) {
        if (e.message !== 'Failed to fetch') throw e;
      }
    }
    throw new Error('Generation timed out. Please try again.');
  };

  const handleGenerate = async () => {
    if (!token) return;
    setGenerating(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/race-plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ race_date: raceDate, benchmarks }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Generation failed');
      }
      const { plan_id } = await resp.json();
      await pollPlanStatus(plan_id);
      setShowForm(false);
      fetchPlans();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!token) return;
    await fetch(`${API_URL}/api/race-plan/${planId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setPlans(prev => prev.filter(p => p.plan_id !== planId));
  };

  const handleToggleShare = async (planId) => {
    if (!token) return;
    const resp = await fetch(`${API_URL}/api/race-plan/${planId}/share`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.ok) {
      const updated = await resp.json();
      setPlans(prev => prev.map(p => p.plan_id === planId ? updated : p));
    }
  };

  if (!user) {
    return (
      <div className="text-center py-12" data-testid="race-plan-login-prompt">
        <Trophy className="w-10 h-10 text-[#e8ff00] mx-auto mb-4" />
        <h3 className="text-white text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Personal Race Plan Generator</h3>
        <p className="text-zinc-400 text-sm mb-4">AI-powered 8-week HYROX training plan personalized to your experience level</p>
        <button
          onClick={() => navigate('/auth')}
          className="px-6 py-3 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          data-testid="race-plan-login-btn"
        >
          Login to Generate Your Plan <ArrowRight className="w-4 h-4 inline ml-1" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="race-plan-generator">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            <Trophy className="w-5 h-5 text-[#e8ff00] inline mr-2" />
            Your Race Plans
          </h3>
          <p className="text-zinc-500 text-xs mt-1">AI-generated training plans based on your profile</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          data-testid="new-plan-btn"
        >
          <Zap className="w-4 h-4" /> Generate New Plan
        </button>
      </div>

      {/* Generate Form */}
      {showForm && (
        <div className="border border-[#e8ff00]/20 bg-zinc-900/60 p-5 mb-6 space-y-4" data-testid="plan-generate-form">
          <p className="text-zinc-400 text-xs">
            We'll use your profile data (experience: <span className="text-[#e8ff00]">{user.experience_level || 'not set'}</span>, goals: <span className="text-[#e8ff00]">{(user.fitness_goals || []).join(', ') || 'not set'}</span>) to personalize your plan.
            <button onClick={() => navigate('/account')} className="text-[#e8ff00] ml-1 hover:underline">Edit profile</button>
          </p>

          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Race Date (optional)</label>
            <input type="date" value={raceDate} onChange={e => setRaceDate(e.target.value)}
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-[#e8ff00] focus:outline-none w-full max-w-xs"
              data-testid="race-date-input" />
          </div>

          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Current Benchmarks (optional)</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {BENCHMARKS.map(b => (
                <div key={b.key}>
                  <label className="text-zinc-600 text-[10px] block mb-1">{b.label}</label>
                  <input type="text" placeholder={b.placeholder}
                    value={benchmarks[b.key] || ''}
                    onChange={e => setBenchmarks(prev => ({ ...prev, [b.key]: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-xs focus:border-[#e8ff00] focus:outline-none"
                    data-testid={`benchmark-${b.key}`} />
                </div>
              ))}
            </div>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-6 py-3 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="generate-plan-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating (30-60 seconds)...</>
            ) : (
              <><Zap className="w-4 h-4" /> Generate 8-Week Plan</>
            )}
          </button>
        </div>
      )}

      {/* Plans List */}
      {plans.length > 0 ? (
        <div className="space-y-8">
          {plans.map(p => <PlanViewer key={p.plan_id} planDoc={p} onDelete={handleDelete} onToggleShare={handleToggleShare} token={token} />)}
        </div>
      ) : !showForm && (
        <div className="text-center py-8 border border-zinc-800 border-dashed">
          <Dumbbell className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No race plans yet</p>
          <p className="text-zinc-600 text-xs mt-1">Generate your first AI-powered training plan</p>
        </div>
      )}
    </div>
  );
}
